import React, { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence, LayoutGroup, useDragControls } from "framer-motion";
import { ArrowUpRight, ChevronLeft, X } from "lucide-react";

const NAV_LINKS = [
  {
    id: "how-it-works",
    label: "How it works",
    index: "01",
    description: "A clean recovery loop that fixes failed payments automatically.",
  },
  {
    id: "features",
    label: "What you get?",
    index: "02",
    description: "A complete recovery toolkit: dashboard, support, and integrations.",
  },
  {
    id: "offer",
    label: "Pricing",
    index: "03",
    description: "Pure performance pricing. Pay nothing unless we recover.",
  },
  {
    id: "mission",
    label: "Our Mission",
    index: "04",
    description: "Why we exist and what we’re building for South African businesses.",
  },
];

const SPRING = { type: "spring", stiffness: 320, damping: 32, mass: 0.7 };

/* Animated hamburger that morphs lines → X */
function HamburgerIcon({ open, color }) {
  const lineStyle = {
    height: 1.5,
    width: 18,
    background: color,
    borderRadius: 2,
    display: "block",
    transition: "background 0.3s",
    transformOrigin: "center",
  };
  return (
    <span className="relative inline-flex flex-col items-center justify-center gap-[5px] w-6 h-6">
      <motion.span
        animate={open ? { rotate: 45, y: 6.5 } : { rotate: 0, y: 0 }}
        transition={{ duration: 0.32, ease: [0.22, 1, 0.36, 1] }}
        style={lineStyle}
      />
      <motion.span
        animate={open ? { opacity: 0, x: -8 } : { opacity: 1, x: 0 }}
        transition={{ duration: 0.18 }}
        style={lineStyle}
      />
      <motion.span
        animate={open ? { rotate: -45, y: -6.5 } : { rotate: 0, y: 0 }}
        transition={{ duration: 0.32, ease: [0.22, 1, 0.36, 1] }}
        style={lineStyle}
      />
    </span>
  );
}

export default function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [hoveredLink, setHoveredLink] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileView, setMobileView] = useState("root");
  const dragControls = useDragControls();
  const selectedMobileLink = useMemo(
    () => NAV_LINKS.find((l) => l.id === mobileView),
    [mobileView],
  );

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 80);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const mql = window.matchMedia("(min-width: 768px)");
    const onChange = () => {
      if (mql.matches) setMobileOpen(false);
    };
    onChange();
    if (mql.addEventListener) mql.addEventListener("change", onChange);
    else mql.addListener(onChange);
    return () => {
      if (mql.removeEventListener) mql.removeEventListener("change", onChange);
      else mql.removeListener(onChange);
    };
  }, []);

  // Lock body scroll when mobile drawer open
  useEffect(() => {
    document.body.style.overflow = mobileOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [mobileOpen]);

  useEffect(() => {
    if (!mobileOpen) setMobileView("root");
  }, [mobileOpen]);

  useEffect(() => {
    if (!mobileOpen) return;
    const onKeyDown = (e) => {
      if (e.key === "Escape") setMobileOpen(false);
      if (e.key === "Backspace" && mobileView !== "root") setMobileView("root");
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [mobileOpen, mobileView]);

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    setMobileOpen(false);
  };

  const pillBg = scrolled ? "rgba(14, 12, 19, 0.82)" : "rgba(14, 12, 19, 0.60)";
  const pillBorder = scrolled
    ? "1px solid rgba(255,255,255,0.10)"
    : "1px solid rgba(255,255,255,0.08)";
  const textColor = "rgba(237, 228, 208, 0.72)";
  const textColorBold = "rgba(237, 228, 208, 0.95)";
  const hamburgerColor = "rgba(237, 228, 208, 0.92)";
  const pillShadow = scrolled
    ? "0 1px 0 rgba(255,255,255,0.05) inset, 0 14px 50px rgba(0,0,0,0.55), 0 0 0 1px rgba(0,0,0,0.10)"
    : "0 1px 0 rgba(255,255,255,0.04) inset, 0 10px 40px rgba(0,0,0,0.45), 0 0 0 1px rgba(0,0,0,0.08)";
  const mobileToggleBg = mobileOpen ? "rgba(255,255,255,0.085)" : "rgba(255,255,255,0.05)";
  const mobileToggleBorder = mobileOpen ? "1px solid rgba(255,255,255,0.14)" : "1px solid rgba(255,255,255,0.10)";

  return (
    <header
      data-testid="site-nav"
      className="fixed top-0 left-0 right-0 z-[100] flex justify-center pt-3 px-4 md:pt-[14px] md:px-4"
    >
      <LayoutGroup>
        <motion.div
          layout
          transition={SPRING}
          className="w-full max-w-[960px] lg:max-w-[1040px] xl:max-w-[1180px] h-[52px] md:h-[56px] pl-4 pr-3 rounded-full grid grid-cols-[auto_1fr_auto] items-center"
          style={{
            backgroundColor: pillBg,
            backgroundImage: scrolled
              ? "linear-gradient(180deg, rgba(255,255,255,0.05), rgba(255,255,255,0))"
              : "linear-gradient(180deg, rgba(255,255,255,0.04), rgba(255,255,255,0))",
            border: pillBorder,
            backdropFilter: "blur(18px) saturate(160%)",
            WebkitBackdropFilter: "blur(18px) saturate(160%)",
            boxShadow: pillShadow,
            transition:
              "background-color 0.35s ease, border-color 0.35s ease, box-shadow 0.35s ease, background-image 0.35s ease",
          }}
        >
          <motion.button
            layout="position"
            transition={SPRING}
            onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
            className="flex items-center shrink-0"
            data-testid="logo-button"
            aria-label="Crown Ledger"
          >
            <img
              src="/New_Logo_Crown.png"
              alt="Crown Ledger"
              className="h-[40px] w-auto block"
              style={{
                opacity: scrolled ? 0.92 : 1,
                transition: "opacity 0.35s ease",
              }}
            />
          </motion.button>

          <AnimatePresence initial={false}>
            <motion.nav
              key="nav-links"
              initial={{ opacity: 0, y: -3 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -3 }}
              transition={{ duration: 0.18, ease: [0.22, 1, 0.36, 1] }}
              onMouseLeave={() => setHoveredLink(null)}
              className="hidden md:flex items-center justify-center min-w-0 mx-auto"
              style={{
                background: "rgba(255,255,255,0.028)",
                border: "1px solid rgba(255,255,255,0.08)",
                borderRadius: 9999,
                padding: 3,
                gap: 1,
              }}
            >
              {NAV_LINKS.map((link) => (
                <button
                  key={link.id}
                  onMouseEnter={() => setHoveredLink(link.id)}
                  onClick={() => scrollTo(link.id)}
                  className="relative px-3.5 py-1.5 rounded-full text-[12.5px] font-body font-medium transition-colors duration-200"
                  style={{
                    color: hoveredLink === link.id ? textColorBold : textColor,
                    letterSpacing: "0.01em",
                  }}
                  data-testid={`nav-link-${link.id}`}
                >
                  {hoveredLink === link.id && (
                    <motion.span
                      layoutId="navHoverPill"
                      className="absolute inset-0 rounded-full"
                      style={{
                        background: "rgba(255,255,255,0.055)",
                        border: "1px solid rgba(255,255,255,0.09)",
                      }}
                      transition={{
                        type: "spring",
                        stiffness: 420,
                        damping: 34,
                      }}
                    />
                  )}
                  <span className="relative z-10">{link.label}</span>
                </button>
              ))}
            </motion.nav>
          </AnimatePresence>

          <motion.button
            layout="position"
            transition={SPRING}
            onClick={() => scrollTo("calculator")}
            className="hidden md:inline-flex justify-self-end items-center gap-2 rounded-full px-4 py-2 text-[12.5px] font-body font-semibold"
            data-testid="nav-cta-gain-access"
            style={{
              background: "linear-gradient(180deg, rgba(255,255,255,0.98), rgba(237,228,208,0.95))",
              color: "#09080A",
              border: scrolled ? "1px solid rgba(255,255,255,0.16)" : "1px solid rgba(255,255,255,0.12)",
              boxShadow:
                "0 1px 0 rgba(255,255,255,0.50) inset, 0 -1px 0 rgba(0,0,0,0.10) inset, 0 10px 28px rgba(0,0,0,0.40)",
            }}
            whileHover={{ y: -1, scale: 1.01 }}
            whileTap={{ scale: 0.98 }}
            aria-label="Gain Access"
          >
            <span>Gain Access</span>
            <ArrowUpRight size={16} />
          </motion.button>

          <motion.button
            className="md:hidden justify-self-end inline-flex items-center justify-center w-10 h-10 rounded-full"
            onClick={() => setMobileOpen((v) => !v)}
            aria-label="Toggle menu"
            data-testid="nav-mobile-toggle"
            style={{
              background: mobileToggleBg,
              border: mobileToggleBorder,
              boxShadow:
                "0 1px 0 rgba(255,255,255,0.06) inset, 0 10px 28px rgba(0,0,0,0.40), 0 0 0 1px rgba(0,0,0,0.08)",
            }}
            whileTap={{ scale: 0.96 }}
          >
            <HamburgerIcon open={mobileOpen} color={hamburgerColor} />
          </motion.button>
        </motion.div>
      </LayoutGroup>

      <AnimatePresence>
        {mobileOpen && (
          <motion.div key="mobile-drawer" className="md:hidden fixed inset-0 z-[90]" data-testid="nav-mobile-drawer">
            <motion.button
              type="button"
              aria-label="Close menu"
              onClick={() => setMobileOpen(false)}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="absolute inset-0"
              style={{
                background: "rgba(6, 5, 10, 0.62)",
                backdropFilter: "blur(8px) saturate(140%)",
                WebkitBackdropFilter: "blur(8px) saturate(140%)",
              }}
            />

            <motion.aside
              initial={{ y: "8%", opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: "8%", opacity: 0 }}
              transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
              drag="y"
              dragControls={dragControls}
              dragListener={false}
              dragElastic={0.06}
              dragConstraints={{ top: 0, bottom: 0 }}
              onDragEnd={(_, info) => {
                if (info.offset.y > 90) setMobileOpen(false);
              }}
              className="absolute inset-x-0 bottom-0 w-full"
              style={{
                height: "min(720px, 92dvh)",
                background: "rgba(10, 8, 15, 0.94)",
                border: "1px solid rgba(255,255,255,0.10)",
                borderBottom: "0",
                borderTopLeftRadius: 26,
                borderTopRightRadius: 26,
                backdropFilter: "blur(22px) saturate(160%)",
                WebkitBackdropFilter: "blur(22px) saturate(160%)",
                boxShadow: "0 -20px 80px rgba(0,0,0,0.70)",
              }}
              role="dialog"
              aria-modal="true"
              aria-label="Menu"
            >
              <div
                className="absolute inset-x-0 top-0 h-16"
                style={{ zIndex: 1 }}
                onPointerDown={(e) => dragControls.start(e)}
                aria-hidden="true"
              />
              <div
                className="absolute left-1/2 -translate-x-1/2 top-3 h-1 w-12 rounded-full pointer-events-none"
                style={{ background: "rgba(255,255,255,0.18)", zIndex: 2 }}
              />
              <div
                className="absolute pointer-events-none"
                style={{
                  top: "0%",
                  left: "-20%",
                  width: 420,
                  height: 420,
                  background: "radial-gradient(circle, rgba(184,146,42,0.14), transparent 70%)",
                  filter: "blur(60px)",
                }}
              />
              <div
                className="absolute pointer-events-none"
                style={{
                  bottom: "-10%",
                  right: "-20%",
                  width: 420,
                  height: 420,
                  background: "radial-gradient(circle, rgba(102,54,157,0.16), transparent 70%)",
                  filter: "blur(60px)",
                }}
              />

              <div className="relative h-full flex flex-col" style={{ paddingTop: 18, paddingBottom: "calc(22px + env(safe-area-inset-bottom))" }}>
                {mobileView !== "root" ? (
                  <button
                    type="button"
                    onClick={() => setMobileView("root")}
                    className="absolute left-5 top-5 inline-flex items-center gap-2 rounded-full px-3 py-2"
                    style={{
                      background: "rgba(255,255,255,0.055)",
                      border: "1px solid rgba(255,255,255,0.10)",
                      color: "rgba(237,228,208,0.92)",
                    }}
                    aria-label="Go back"
                    data-testid="nav-mobile-back"
                  >
                    <ChevronLeft size={16} />
                    <span className="font-body text-[13px] font-medium">Back</span>
                  </button>
                ) : null}

                <button
                  type="button"
                  onClick={() => setMobileOpen(false)}
                  className="absolute right-5 top-5 inline-flex items-center justify-center rounded-full w-10 h-10"
                  style={{
                    background: "rgba(255,255,255,0.055)",
                    border: "1px solid rgba(255,255,255,0.10)",
                    color: "rgba(237,228,208,0.92)",
                    boxShadow: "0 10px 28px rgba(0,0,0,0.40)",
                    zIndex: 3,
                  }}
                  aria-label="Close"
                  data-testid="nav-mobile-close"
                >
                  <X size={18} />
                </button>

                <div className="flex-1 overflow-hidden">
                  <AnimatePresence mode="wait" initial={false}>
                    {mobileView === "root" ? (
                      <motion.div
                        key="mobile-root"
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -10 }}
                        transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
                        className="h-full flex flex-col"
                      >
                        <nav className="flex-1 px-5 pt-16 overflow-y-auto">
                          {NAV_LINKS.map((link, i) => (
                            <motion.button
                              key={link.id}
                              initial={{ opacity: 0, y: 10 }}
                              animate={{ opacity: 1, y: 0 }}
                              transition={{
                                delay: 0.06 + i * 0.05,
                                ease: [0.22, 1, 0.36, 1],
                                duration: 0.35,
                              }}
                              onClick={() => scrollTo(link.id)}
                              className="w-full flex items-center justify-between py-4 group border-b"
                              style={{ borderColor: "rgba(255,255,255,0.075)" }}
                              data-testid={`nav-mobile-${link.id}`}
                            >
                              <span
                                className="font-syne text-[16px] font-semibold"
                                style={{ color: "rgba(237, 228, 208, 0.95)" }}
                              >
                                {link.label}
                              </span>
                              <ArrowUpRight
                                size={18}
                                className="mt-1 text-mut transition-all duration-300 group-hover:text-white-soft group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                              />
                            </motion.button>
                          ))}

                          <div className="pt-6 pb-2">
                            <button
                              type="button"
                              onClick={() => scrollTo("calculator")}
                              className="btn-primary w-full"
                              style={{ padding: "16px 24px" }}
                              data-testid="nav-mobile-cta-gain-access"
                            >
                              <span className="flex items-center justify-center gap-2">
                                Gain Access
                                <ArrowUpRight size={16} />
                              </span>
                            </button>
                          </div>
                        </nav>
                      </motion.div>
                    ) : (
                      <motion.div
                        key="mobile-detail"
                        initial={{ opacity: 0, x: 10 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: 10 }}
                        transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
                        className="h-full px-6 pt-16 flex flex-col"
                      >
                        <div className="flex items-start justify-between gap-4">
                          <div>
                            <div className="font-mono text-[11px] tracking-[0.18em] text-mut uppercase">
                              {selectedMobileLink?.index}
                            </div>
                            <div className="mt-2 font-display font-semibold text-[34px] leading-[1.05] text-pri">
                              {selectedMobileLink?.label}
                            </div>
                            {selectedMobileLink?.description ? (
                              <div className="mt-4 font-body text-[15px] text-white-soft" style={{ lineHeight: 1.65 }}>
                                {selectedMobileLink.description}
                              </div>
                            ) : null}
                          </div>
                        </div>

                        <div className="mt-7 grid gap-3">
                          <button
                            type="button"
                            onClick={() => scrollTo(selectedMobileLink?.id)}
                            className="btn-primary w-full"
                            style={{ padding: "16px 24px" }}
                            data-testid="nav-mobile-go"
                          >
                            <span className="flex items-center gap-2">
                              Go to section
                              <ArrowUpRight size={16} />
                            </span>
                          </button>
                        </div>

                        <div className="mt-auto pt-6">
                          <div className="rounded-2xl p-4" style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)" }}>
                            <div className="font-mono text-[10px] tracking-[0.20em] text-mut uppercase">Contact</div>
                            <div className="mt-2 font-body text-[14px] text-white-soft">hello@crownledger.co.za</div>
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </div>
            </motion.aside>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
